import { useMutation } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { postData } from '../../utils/api';
import { PaymentAuthResult, PaymentProvider } from '../../types/payment';
import { usePaymentResponseHandler } from '../usePaymentResponseHandler';
import { API_ROUTES } from '../../constants/apiRoutes';

type KapopayDepositRequest = {
  amount: number;
  redirectUrl: string;
};

type KapopayWithdrawRequest = {
  amount: number;
  beneficiaryName: string;
  IBAN: string;
  swiftCode: string;
  bankName: string;
};

type KapopayResponse = {
  redirectUrl?: string;
  status: 'APPROVED' | 'DECLINED' | 'ERROR';
  errorMessage?: string;
  authResults?: PaymentAuthResult[];
};

const NO_PROVIDERS: PaymentProvider[] = [];

export const useKapopay = () => {
  const { handlePaymentSuccess, handlePaymentError } = usePaymentResponseHandler();

  const depositMutation = useMutation<KapopayResponse, Error, KapopayDepositRequest>({
    mutationFn: (data) => postData(API_ROUTES.KAPOPAY.DEPOSIT, data, 'wallet'),
    onSuccess: (response) => {
      if (!isEmpty(response.authResults)) {
        handlePaymentSuccess(response, {
          successMessage: 'depositRequestSubmitted',
        });
      }

      if (response.redirectUrl) {
        window.location.href = response.redirectUrl;
      }
    },
    onError: (error) => {
      handlePaymentError(error, {
        failureMessage: 'depositRequestFailed',
      });
    },
  });

  const withdrawMutation = useMutation<KapopayResponse, Error, KapopayWithdrawRequest>({
    mutationFn: (data) => postData(API_ROUTES.KAPOPAY.WITHDRAW, data, 'wallet'),
    onSuccess: (response) => {
      handlePaymentSuccess(response, {
        successMessage: 'withdrawRequestSubmitted',
      });
    },
    onError: (error) => {
      handlePaymentError(error, {
        failureMessage: 'withdrawRequestFailed',
      });
    },
  });

  return { depositMutation, withdrawMutation, paymentProviders: NO_PROVIDERS };
};
